import { useEffect, type FC } from 'react'
import { useTranslation } from 'react-i18next'

import { Card, List, ListItem, ListItemIllustration, ListItemText, Skeleton } from '@dhl-ui/components'
import { ChevronRightIcon } from '@dhl-ui/icons'

import { useWizardContext, useSetTitle } from '~/setup/model/context.js'
import { useGetProfileLastAddedAddress } from '~/profile/model/profile/hooks.js'
import { useGetDeliveryPreferenceForAddress } from '~/profile/view/preferences/hooks/useGetDeliveryPreferenceForAddress.js'
import { DeliveryPreferenceIllustration } from '~/profile/view/preferences/components/DeliveryPreferenceIllustration.js'

const ListItemSkeleton: FC = () => (
  <ListItem alignItems="center">
    <ListItemIllustration>
      <Skeleton variant="rounded" width={48} height={48} />
    </ListItemIllustration>
    <ListItemText
      primary={<Skeleton variant="text" width={140} />}
      secondary={<Skeleton variant="text" width={200} />}
    />
  </ListItem>
)

const DeliveryPreferenceChoiceSkeleton: FC = () => (
  <List>
    <ListItemSkeleton />
    <ListItemSkeleton />
    <ListItemSkeleton />
  </List>
)

export const DeliveryPreferenceChoice: FC = () => {
  const { t } = useTranslation()
  const { setCurrentStepCanContinue, setWizardState, nextStep } = useWizardContext()

  useSetTitle(t('portal.profileSetup.deliveryPreference.pageTitle'))

  const { data: lastAddedAddress, isFetching } = useGetProfileLastAddedAddress()
  const { data: preferenceData, isLoading: isLoadingPreference } = useGetDeliveryPreferenceForAddress(
    lastAddedAddress?.addressId
  )

  const handleOptionClick = (preference: 'toDoor' | 'servicePoint' | 'agreedPlace') => {
    setWizardState('deliveryPreference', preference)
    nextStep()
  }

  useEffect(() => {
    setCurrentStepCanContinue(false)
  }, [setCurrentStepCanContinue])

  const isLoading = (isFetching || isLoadingPreference) && !preferenceData
  const preference = preferenceData?.preference

  return (
    <Card withPaddingTop>
      <Card.Header title={t('portal.profileSetup.deliveryPreference.choiceCardTitle')} />
      <Card.Content noPaddingBottom noPaddingSides noPaddingTop>
        {isLoading && <DeliveryPreferenceChoiceSkeleton />}

        {!isLoading && (
          <List>
            <ListItem
              isButton
              alignItems="center"
              slotProps={{
                secondaryIcon: <ChevronRightIcon />,
              }}
              onClick={() => handleOptionClick('toDoor')}
            >
              <ListItemIllustration>
                <DeliveryPreferenceIllustration type="DoorDelivery" />
              </ListItemIllustration>
              <ListItemText
                emphasis
                primary={t('portal.profile.preferences.options.DoorDelivery.title')}
                secondary={t('portal.profile.preferences.options.DoorDelivery.description')}
              />
            </ListItem>
            {preference?.availableOptions?.isServicePointAvailable && (
              <ListItem
                isButton
                alignItems="center"
                slotProps={{
                  secondaryIcon: <ChevronRightIcon />,
                }}
                onClick={() => handleOptionClick('servicePoint')}
              >
                <ListItemIllustration>
                  <DeliveryPreferenceIllustration type="ServicePointDelivery" />
                </ListItemIllustration>
                <ListItemText
                  emphasis
                  primary={t('portal.profile.preferences.options.ServicePointDelivery.title')}
                  secondary={t('portal.profile.preferences.options.ServicePointDelivery.description')}
                />
              </ListItem>
            )}
            {preference?.availableOptions?.isAgreedPlaceAvailable && (
              <ListItem
                isButton
                alignItems="center"
                slotProps={{
                  secondaryIcon: <ChevronRightIcon />,
                }}
                onClick={() => handleOptionClick('agreedPlace')}
              >
                <ListItemIllustration>
                  <DeliveryPreferenceIllustration type="AgreedPlaceDelivery" />
                </ListItemIllustration>
                <ListItemText
                  emphasis
                  primary={t('portal.profile.preferences.options.AgreedPlaceDelivery.title')}
                  secondary={t('portal.profile.preferences.options.AgreedPlaceDelivery.description')}
                />
              </ListItem>
            )}
          </List>
        )}
      </Card.Content>
    </Card>
  )
}
